<script setup lang="ts">
import MarketingFlowTriggerTypeBaseWrap from './BaseWrap.vue'

import type { LoyaltyReward } from '@/types/types'
import type { TriggerSettings } from '../../types/marketingFlow'

const { t } = useI18n()

const { getTriggerStepErrors } = useMarketingFlow()

const triggerSettings = defineModel<TriggerSettings>('triggerSettings')

const props = defineProps<{
  rewards: LoyaltyReward[]
}>()

const emit = defineEmits<{
  (e: 'onChanges', { isCritical, field, value }): void
}>()

const filteredRewards = computed(() => {
  if (triggerSettings.value.triggerType === 'REWARD_NEAR_TO_UNLOCK' && props.rewards) {
    return props.rewards.filter((reward) => reward.usages.includes('loyalty'))
  }

  return props.rewards ?? []
})

const triggerStepErrors = computed(() => {
  return getTriggerStepErrors(triggerSettings.value)
})

const pointTresholdError = computed(() => {
  const pointTresholdErrors = triggerStepErrors.value.find((error) => error.field === 'pointTreshold')

  return pointTresholdErrors && triggerSettings.value.pointTreshold !== null
    ? [t(`pages.marketingFlow.marketingFlowEditPage.error.${pointTresholdErrors?.i18nKey}`)]
    : null
})

const handleSelectReward = (index: number, rewardId: number) => {
  triggerSettings.value.selectedRewards[index] = rewardId
  addNullRewardToShowAddButton()

  emit('onChanges', { isCritical: true, field: 'selectedRewards', value: triggerSettings.value.selectedRewards })
}

const addNullRewardToShowAddButton = () => {
  const lastIndex = triggerSettings.value.selectedRewards.length - 1
  if (triggerSettings.value.selectedRewards[lastIndex] !== null) {
    triggerSettings.value.selectedRewards.push(null)
  }
}

const handleClearReward = (index: number) => {
  triggerSettings.value.selectedRewards[index] = null
  triggerSettings.value.selectedRewards = triggerSettings.value.selectedRewards.filter((rewardId) => rewardId !== null)

  addNullRewardToShowAddButton()

  emit('onChanges', { isCritical: true, field: 'selectedRewards', value: triggerSettings.value.selectedRewards })
}

onMounted(() => {
  addNullRewardToShowAddButton()
})
</script>

<template>
  <MarketingFlowTriggerTypeBaseWrap
    icon="IconoirUserStar"
    :title="t('pages.marketingFlow.marketingFlowEditPage.trigger.reward')"
  >
    <div v-if="rewards" class="flex flex-col gap-1.5">
      <label class="text-sm text-foreground">
        {{ t('pages.marketingFlow.marketingFlowEditPage.trigger.whichReward') }}
        <span class="text-red-500">*</span>
      </label>
      <div class="flex flex-col gap-3 p-4 rounded-lg border bg-white">
        <RewardSelector
          v-for="(reward, index) in triggerSettings.selectedRewards"
          :key="reward"
          :selected-reward-id="reward"
          :rewards="filteredRewards"
          :hidden-reward-ids="triggerSettings.selectedRewards.filter((rewardId) => rewardId !== null)"
          required
          :button-shown="['edit', 'change', 'clear']"
          :show-points="triggerSettings.triggerType === 'REWARD_NEAR_TO_UNLOCK'"
          no-border-frame
          add-button-class="w-full mx-0! rounded-lg!"
          reward-item-class="rounded-lg!"
          @clear-reward="handleClearReward(index)"
          @select-reward="(rewardId: number) => handleSelectReward(index, rewardId)"
        />
      </div>
    </div>
    <div v-if="triggerSettings.triggerType === 'REWARD_NEAR_TO_UNLOCK'" class="flex flex-col mt-4 gap-1.5">
      <div class="flex items-center gap-1.5">
        <label for="pointTreshold" class="max-w-max text-sm text-foreground">
          {{ t('pages.marketingFlow.marketingFlowEditPage.trigger.pointTreshold') }}
          <span class="text-red-500">*</span>
        </label>
        <AppTooltip
          tooltip-position="right"
          :text="t('pages.marketingFlow.marketingFlowEditPage.trigger.pointTresholdTooltip')"
          width="max-w-xs"
        >
          <BaseIcon name="IconoirInfoCircle" class="h-4 w-4 cursor-pointer text-foreground-secondary" />
        </AppTooltip>
      </div>

      <AppFormInput
        id="pointTreshold"
        v-model="triggerSettings.pointTreshold"
        :errors="pointTresholdError"
        error-classes="text-xs"
        @update:model-value="emit('onChanges', { isCritical: true, field: 'pointTreshold', value: $event })"
      >
        <template #icon-right>
          <span class="text-sm text-muted-foreground">{{ t('global.words.points') }}</span>
        </template>
      </AppFormInput>
    </div>
  </MarketingFlowTriggerTypeBaseWrap>
</template>
